/**
 * Phase: "complete" — robot has reached the current destination. For
 * multi-stop trips in AUTO nav mode, shows a 5-second countdown then
 * auto-advances to the next stop; otherwise shows acknowledgment with
 * Return to Base / Next Stop / Choose Another Location.
 *
 * The auto-advance countdown is gated on `navMode === "AUTO"` (RoomNumberPageAero
 * threads a working navMode picker here, unlike some ports where it's inert).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Avatar, Typography, Button, CircularProgress, TextField } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NavigationIcon from "@mui/icons-material/Navigation";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PropTypes from "prop-types";

import DeliveryLayout from "../../../components/delivery/DeliveryLayout";
import SectionCard from "../../../components/delivery/SectionCard";
import ActionButtonRow from "../../../components/delivery/ActionButtonRow";
import DoorControl from "../../../components/delivery/DoorControl";
import TripStatTiles from "../../../components/delivery/TripStatTiles";
import StopInfoStrip from "../../../components/delivery/StopInfoStrip";
import { getStopLabel } from "../deliveryConstants";
import { actionBtnSx, getDividerSx } from "../deliveryStyles";
import { tokens } from "../../../theme";

const AUTO_ADVANCE_SECONDS = 5;

const formatClock = (ts) =>
  ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;

const CompletePhase = ({
  selectedMap,
  navigationTarget,
  robot,
  fromRobotId,
  batteryLevel,
  batteryCharging,
  showMapSelectionModalDialog,
  closeMapSelectionDialogModal,
  portalRobots,
  onRobotChange,
  onEmergencyResume,
  isRobotConnected,
  isDoorOpen,
  onDoorPress,
  isAeroRobot,
  currentDest,
  destinations = [],
  currentStopIndex = 0,
  hasNextStop,
  navMode = "MANUAL",
  onNextStop,
  onReturnToBase,
  onChangeDestination,
  onRate,
}) => {
  const autoAdvanceEligible = hasNextStop && navMode === "AUTO";
  const [countdown, setCountdown] = useState(autoAdvanceEligible ? AUTO_ADVANCE_SECONDS : null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  // RoomNumberPageAero (this portal) doesn't track a trip-start/arrived
  // timestamp the way admin-fe's orchestrator does (tripStartRef/arrivedAtRef),
  // so "Arrived" is captured once per stop reached — on first render of THIS
  // phase for this stop — rather than re-read on every render, which would
  // otherwise drift to "now" on each re-render instead of the real arrival time.
  const [arrivedAt, setArrivedAt] = useState(() => Date.now());
  useEffect(() => {
    setArrivedAt(Date.now());
  }, [currentStopIndex]);

  const handlePickRating = (value) => {
    if (reviewSubmitted) return;
    setRating(value);
  };

  const handleSubmitReview = () => {
    onRate?.(rating, comment);
    setReviewSubmitted(true);
  };

  const fromLabel = currentStopIndex > 0 ? destinations[currentStopIndex - 1]?.name : "Base";

  const tripStats = useMemo(() => {
    const stats = [];
    if (batteryLevel !== null && batteryLevel !== undefined)
      stats.push({
        label: "Battery",
        value: `${Math.round(batteryLevel * 100) / 100}%`,
        color: tokens.success,
      });
    stats.push({ label: "Arrived", value: formatClock(arrivedAt) });
    return stats;
  }, [batteryLevel, arrivedAt]);

  // Auto-advance countdown — only runs for multi-stop trips in AUTO nav mode.
  useEffect(() => {
    if (!autoAdvanceEligible) {
      setCountdown(null);
      return undefined;
    }
    setCountdown(AUTO_ADVANCE_SECONDS);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onNextStop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvanceEligible, currentStopIndex]);

  useEffect(() => {
    setRating(0);
    setComment("");
    setReviewSubmitted(false);
  }, [currentStopIndex]);

  const nextDest = destinations?.[currentStopIndex + 1];

  let ratingLabel = "Rate this run (optional)";
  if (reviewSubmitted) {
    ratingLabel = "Review submitted ✓";
  } else if (rating) {
    ratingLabel = `Rated ${rating}.0`;
  }

  let subtitle = "";
  if (currentDest) {
    subtitle = currentDest.name;
    if (destinations.length > 1) {
      subtitle += ` — Stop ${getStopLabel(currentStopIndex)} of ${destinations.length}`;
    }
  }

  return (
    <DeliveryLayout
      selectedMap={selectedMap}
      navigationTarget={navigationTarget}
      robot={robot}
      fromRobotId={fromRobotId}
      batteryLevel={batteryLevel}
      batteryCharging={batteryCharging}
      showMapSelectionModalDialog={showMapSelectionModalDialog}
      closeMapSelectionDialogModal={closeMapSelectionDialogModal}
      portalRobots={portalRobots}
      onRobotChange={onRobotChange}
      onEmergencyResume={onEmergencyResume}
      isRobotConnected={isRobotConnected}
      rightPanel={
        <SectionCard
          icon={
            <Avatar
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "rgba(255,255,255,0.15)",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: "#fff" }} />
            </Avatar>
          }
          title="Destination Reached!"
          subtitle={subtitle}
          headerBg="linear-gradient(135deg, #052e16 0%, #166534 100%)"
          sx={{ "& .MuiCardContent-root": { p: 2, gap: 1.5, "&:last-child": { pb: 2 } } }}
        >
          <DoorControl
            isDoorOpen={isDoorOpen}
            onDoorPress={onDoorPress}
            isAeroRobot={isAeroRobot}
          />

          <StopInfoStrip
            from={fromLabel}
            to={currentDest?.name}
            trailingLabel={destinations.length > 1 ? "STOP" : undefined}
            trailingValue={
              destinations.length > 1
                ? `${getStopLabel(currentStopIndex)} of ${destinations.length}`
                : undefined
            }
          />

          <TripStatTiles stats={tripStats} />

          {/* Thank-you + rating — single compact row so the panel never scrolls */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            flexWrap="wrap"
            sx={{
              p: 1.5,
              background: tokens.successSoft,
              border: `1px solid ${tokens.success}`,
              borderRadius: "12px",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: tokens.text,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                THANK YOU!
              </Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: tokens.success }}>
                Task Completed ✓
              </Typography>
            </Box>

            {/* Interactive 5-star rating */}
            <Box sx={{ textAlign: "right" }}>
              <Stack
                direction="row"
                gap={0.25}
                justifyContent="flex-end"
                onMouseLeave={() => setHoverRating(0)}
              >
                {[1, 2, 3, 4, 5].map((s) => {
                  const filled = s <= (hoverRating || rating);
                  const StarCmp = filled ? StarIcon : StarBorderIcon;
                  return (
                    <Box
                      key={s}
                      onClick={() => handlePickRating(s)}
                      onMouseEnter={() => setHoverRating(s)}
                      sx={{
                        cursor: reviewSubmitted ? "default" : "pointer",
                        lineHeight: 0,
                        transition: "transform .12s ease",
                        "&:hover": { transform: reviewSubmitted ? "none" : "scale(1.14)" },
                      }}
                    >
                      <StarCmp sx={{ fontSize: 24, color: tokens.warning }} />
                    </Box>
                  );
                })}
              </Stack>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  mt: 0.25,
                  color: rating ? tokens.success : tokens.text3,
                }}
              >
                {ratingLabel}
              </Typography>
            </Box>
          </Stack>

          {!reviewSubmitted && rating > 0 && (
            <Stack direction="row" gap={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                placeholder="Add a comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                multiline
                maxRows={3}
                sx={{ "& .MuiOutlinedInput-root": { fontSize: 12.5 } }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmitReview}
                sx={{
                  ...actionBtnSx,
                  minWidth: 0,
                  px: 2,
                  background: tokens.success,
                  "&:hover": { background: tokens.success },
                }}
              >
                Submit
              </Button>
            </Stack>
          )}

          <Box sx={getDividerSx()} />

          {/* Auto-advance banner — only for multi-stop trips in AUTO mode */}
          {autoAdvanceEligible && nextDest && countdown !== null && (
            <Box
              sx={{
                p: 1.5,
                background: tokens.accentSoft,
                border: `1px solid ${tokens.accent}`,
                borderLeft: `3px solid ${tokens.accent}`,
                borderRadius: "8px",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
                  <CircularProgress
                    variant="determinate"
                    value={(countdown / AUTO_ADVANCE_SECONDS) * 100}
                    size={36}
                    thickness={4}
                    sx={{ color: tokens.accent }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.accent }}>
                      {countdown}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.accent }}>
                    Auto-navigating to next stop…
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.accent }}>
                    {getStopLabel(currentStopIndex + 1)} — {nextDest.name}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Buttons */}
          <ActionButtonRow>
            {hasNextStop && (
              <Button
                variant="contained"
                onClick={() => {
                  setCountdown(0);
                  onNextStop();
                }}
                startIcon={<NavigationIcon sx={{ fontSize: "15px !important" }} />}
                sx={{
                  ...actionBtnSx,
                  background: tokens.accent,
                  color: "#fff",
                  "&:hover": { background: tokens.accent2 },
                }}
              >
                Go Now → {getStopLabel(currentStopIndex + 1)}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onReturnToBase}
              startIcon={<HomeIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                ...actionBtnSx,
                background: tokens.success,
                color: "#fff",
                "&:hover": { background: tokens.success },
              }}
            >
              Return to Base
            </Button>
            <Button
              variant="outlined"
              onClick={onChangeDestination}
              startIcon={<LocationOnIcon sx={{ fontSize: "15px !important" }} />}
              sx={actionBtnSx}
            >
              Choose Another Location
            </Button>
          </ActionButtonRow>
        </SectionCard>
      }
    />
  );
};

CompletePhase.propTypes = {
  selectedMap: PropTypes.object,
  navigationTarget: PropTypes.object,
  robot: PropTypes.object,
  fromRobotId: PropTypes.string,
  batteryLevel: PropTypes.number,
  batteryCharging: PropTypes.bool,
  showMapSelectionModalDialog: PropTypes.bool,
  closeMapSelectionDialogModal: PropTypes.func,
  portalRobots: PropTypes.array,
  onRobotChange: PropTypes.func,
  onEmergencyResume: PropTypes.func,
  isRobotConnected: PropTypes.bool,
  isDoorOpen: PropTypes.bool,
  onDoorPress: PropTypes.func,
  isAeroRobot: PropTypes.bool,
  currentDest: PropTypes.object,
  destinations: PropTypes.array,
  currentStopIndex: PropTypes.number,
  hasNextStop: PropTypes.bool,
  navMode: PropTypes.oneOf(["MANUAL", "AUTO"]),
  onNextStop: PropTypes.func,
  onReturnToBase: PropTypes.func,
  onChangeDestination: PropTypes.func,
  onRate: PropTypes.func,
};

export default CompletePhase;
